/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useMemo} from 'react';
import {makeStyles} from '@material-ui/core';
import ManufacturerSpreaderDetails from 'manufacturer/ManufacturerSpreaderDetails';
import SpreaderAccordionHeader from 'manufacturer/SpreaderAccordionHeader';
import SpreaderAccordionMessageTime from 'manufacturer/SpreaderAccordionMessageTime';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {isObjectEmpty} from '@ion/core/src/utils/ObjectUtil';
import Accordion from '@ion/dls/src/components/accordion/Accordion';
import {SpreaderAccordion as CurrentSpreaderAccordion} from '@ion/dls/src/components/assetdetails/current/SpreaderAccordion';
import {SkeletonLoader} from '@ion/dls/src/components/loading/SkeletonLoader';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
import {BodyTypography} from '@ion/dls/src/typography/BodyTypography';
import {getDashedDateTime} from '@ion/dls/src/utils/ComponentUtils';
import {AssetDetailsSection, useRegisteredAccordion} from '../assetDetailsAccordionStore';
import {useStyles} from './accordionStyles';
import {SKELETON_COUNT, SKELETON_HEIGHT, skeletonStyles, SpreaderCurrentFallback} from './SpreaderCurrentFallback';
import {getSpreaderLayout} from './spreaderCurrentLayouts';

export interface SpreaderMessageAccordionProps {
  sensorType: string;
  measures: GenericObject;
  messageTime?: string;
  isLoading?: boolean;
}

const useWithStyles = makeStyles(() => ({
  accordionBody : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center',
    padding        : '0px 15px',
    height         : '100%'
  },
  skeletonWrapper : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center',
    gap            : '15px',
    width          : '100%',
    height         : '100%'
  },
  left : {
    height : '100%',
    width  : '60%'
  },
  right : {
    height : '100%',
    width  : '40%'
  },
  text : {
    color    : dlsColor('grey3'),
    fontSize : '14px'
  }
}));

export const SpreaderMessageAccordion = ({
  isLoading,
  measures,
  sensorType,
  messageTime
}: SpreaderMessageAccordionProps) => {
  const styles                 = useWithStyles();
  const classes                = useStyles();
  const translations           = translate(`tracking:spreader.${sensorType}`, {returnObjects: true});
  const shouldShowSpreaderData = !isLoading && !isObjectEmpty(measures);
  const loaders                = Array(SKELETON_COUNT).fill(null);
  const isCurrent              = isCurrentTheme();
  // the current theme's per-manufacturer layout (Force America Type 1 / 2,
  // Dickey-john, Cirus / RoadWatch) - computed only in the current theme,
  // and `null` for any other manufacturer (or in the legacy theme), which
  // falls back to `SpreaderCurrentFallback` rather than this layout ...
  const layout = useMemo(
    () => (isCurrent ? getSpreaderLayout(sensorType, measures) : null),
    [isCurrent, sensorType, measures]
  );
  // registers this section with the Asset Details Drawer's shared accordion
  // store so the drawer header's expand/collapse-all control can drive it -
  // only while the current theme branch below actually renders, the legacy
  // Accordion stays uncontrolled ...
  const {
    expanded : isSpreaderExpanded,
    onExpand : onSpreaderExpand
  } = useRegisteredAccordion(AssetDetailsSection.spreader, false, isCurrent);

  let accordion: React.ReactElement;

  if (isCurrent) {
    // the manufacturer layout needs data - loading, an empty message or an
    // unknown manufacturer falls back to the section card's own states ...
    accordion = shouldShowSpreaderData && layout ? (
      <CurrentSpreaderAccordion title={translate('tracking:spreader.header')}
        receivedTime={messageTime ? getDashedDateTime(messageTime) : undefined}
        {...layout}
        expanded={isSpreaderExpanded}
        onExpand={onSpreaderExpand} />
    ) : (
      <SpreaderCurrentFallback isLoading={isLoading}
        hasData={shouldShowSpreaderData}
        measures={measures}
        sensorType={sensorType}
        translations={translations}
        messageTime={messageTime}
        expanded={isSpreaderExpanded}
        onExpand={onSpreaderExpand} />
    );
  } else {
    accordion = (
      <div className={classes.accordionWrapper}>
        <Accordion className={classes.assetDetailAccordion}
          header={<SpreaderAccordionHeader />}
          details={
            <>
              {shouldShowSpreaderData ? (
                <div className={classes.assetCardListContentWrapper}>
                  <ManufacturerSpreaderDetails measures={measures}
                    sensorType={sensorType}
                    translations={translations} />
                  <SpreaderAccordionMessageTime messageTime={messageTime} />
                </div>
              ) : (
                <div className={styles.accordionBody}>
                  {isLoading ? (
                    <div className={styles.skeletonWrapper}>
                      <div className={styles.left}>
                        {loaders.map(function(_, index) {
                          return (
                            <SkeletonLoader key={index}
                              height={SKELETON_HEIGHT}
                              styles={skeletonStyles} />
                          );
                        })}
                      </div>
                      <div className={styles.right}>
                        {loaders.map(function(_, index) {
                          return (
                            <SkeletonLoader key={index}
                              height={SKELETON_HEIGHT}
                              styles={skeletonStyles} />
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <BodyTypography className={styles.text} variant={'body2'}>
                      {translate('tracking:spreader.noData')}
                    </BodyTypography>
                  )}
                </div>
              )}
            </>
          }
          borderBottom={'none'}
          padding={'15px 0'}
          isCondensedVariant
          defaultExpanded />
      </div>
    );
  }

  return accordion;
};
